import { useState, useEffect } from 'react';
import axios from 'axios';
import { GraduationCap, MapPin, Clock, ExternalLink, ChevronDown, ChevronUp, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Formation type colors (compact version)
const TYPE_COLORS = {
  university: 'bg-purple-50 text-purple-600',
  haute_ecole: 'bg-blue-50 text-blue-600',
  promotion_sociale: 'bg-green-50 text-green-600',
  ifapme: 'bg-orange-50 text-orange-600',
  certification: 'bg-pink-50 text-pink-600',
};

// Region configurations by language
const REGION_CONFIG = {
  fr: {
    regions: [
      { id: 'all', label: 'Toutes les régions', value: null },
      { id: 'brussels_fr', label: 'Bruxelles', value: 'brussels_fr' },
      { id: 'wallonia', label: 'Wallonie', value: 'wallonia' },
    ]
  },
  nl: {
    regions: [
      { id: 'all', label: 'Alle regio\'s', value: null },
      { id: 'brussels_nl', label: 'Brussel', value: 'brussels_nl' },
      { id: 'flanders', label: 'Vlaanderen', value: 'flanders' },
    ]
  }
};

export default function CareerFormations({ careerName, language = 'fr', autoExpand = true }) {
  const [allFormations, setAllFormations] = useState([]);
  const [filteredFormations, setFilteredFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(autoExpand);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('all');

  const labels = {
    fr: {
      title: 'Où se former ?',
      noFormations: 'Aucune formation trouvée',
      noFormationsInRegion: 'Aucune formation dans cette région',
      showMore: 'Voir les formations',
      showLess: 'Masquer',
      duration: 'Durée',
      visit: 'Voir',
      filterByRegion: 'Filtrer par région',
    },
    nl: {
      title: 'Waar studeren?',
      noFormations: 'Geen opleidingen gevonden',
      noFormationsInRegion: 'Geen opleidingen in deze regio',
      showMore: 'Toon opleidingen',
      showLess: 'Verbergen',
      duration: 'Duur',
      visit: 'Bekijk',
      filterByRegion: 'Filter op regio',
    }
  };

  const t = labels[language];
  const regionConfig = REGION_CONFIG[language] || REGION_CONFIG.fr;

  // Fetch all formations for this career
  useEffect(() => {
    const fetchFormations = async () => {
      if (!careerName) return;
      
      try {
        setLoading(true);
        const response = await axios.get(
          `${API}/api/formations/for-career/${encodeURIComponent(careerName)}?language=${language}`
        );
        const formations = response.data.formations || [];
        setAllFormations(formations);
        setFilteredFormations(formations);
        setError(null);
      } catch (err) {
        console.error('Error fetching formations:', err);
        setError('Erreur');
        setAllFormations([]);
        setFilteredFormations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFormations();
  }, [careerName, language]);

  // Filter formations when region changes
  useEffect(() => {
    if (selectedRegion === 'all') {
      setFilteredFormations(allFormations);
    } else {
      const filtered = allFormations.filter(formation => {
        // Check if formation's regions include the selected region
        const regionLabels = formation.regions || [];
        const selectedRegionConfig = regionConfig.regions.find(r => r.id === selectedRegion);
        if (!selectedRegionConfig) return true;
        
        // Match by region label (e.g., "Bruxelles", "Wallonie", "Vlaanderen")
        return regionLabels.some(label => 
          label.toLowerCase().includes(selectedRegionConfig.label.toLowerCase()) ||
          selectedRegionConfig.label.toLowerCase().includes(label.toLowerCase())
        );
      });
      setFilteredFormations(filtered);
    }
  }, [selectedRegion, allFormations, regionConfig.regions]);

  if (loading) {
    return (
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    );
  }

  if (error || allFormations.length === 0) {
    return null; // Don't show anything if no formations
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4" data-testid={`formations-section-${careerName}`}>
      {/* Toggle button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-[#F5F0FF] hover:bg-[#EDE5FF] rounded-lg transition group"
        data-testid="toggle-formations-btn"
      >
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-[#A4187A]" />
          <span className="font-medium text-[#A4187A] text-sm">
            {t.title} ({allFormations.length})
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[#A4187A]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#A4187A]" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-3 space-y-3" data-testid="formations-list">
          {/* Region filter */}
          {allFormations.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="region-filter">
              <div className="flex items-center gap-1 text-xs text-[#1D1D1D]/60">
                <Filter className="w-3 h-3" />
                <span>{t.filterByRegion}:</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                {regionConfig.regions.map((region) => (
                  <button
                    key={region.id}
                    onClick={() => setSelectedRegion(region.id)}
                    className={`px-2 py-1 text-xs rounded-full transition-all ${
                      selectedRegion === region.id
                        ? 'bg-[#A4187A] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`region-filter-${region.id}`}
                  >
                    {region.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Formations list */}
          {filteredFormations.length === 0 ? (
            <p className="text-sm text-[#1D1D1D]/50 text-center py-3">
              {t.noFormationsInRegion}
            </p>
          ) : (
            <>
              {filteredFormations.slice(0, 4).map((formation, idx) => (
                <div 
                  key={formation.id || idx}
                  className="p-3 bg-white border border-gray-100 rounded-lg hover:border-[#A4187A]/20 transition"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Type badge */}
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[formation.type] || 'bg-gray-50 text-gray-600'}`}>
                        {formation.type_label}
                      </span>
                      
                      {/* Formation name */}
                      <h4 className="font-medium text-[#1D1D1D] text-sm mt-1 line-clamp-1">
                        {formation.name}
                      </h4>
                      
                      {/* Institution */}
                      <p className="text-xs text-[#1D1D1D]/60 mt-0.5">
                        {formation.institution}
                      </p>
                      
                      {/* Details row */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-[#1D1D1D]/50">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formation.duration}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {formation.city}
                        </span>
                      </div>
                    </div>
                    
                    {/* Visit button */}
                    <a
                      href={formation.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-[#A4187A] text-white text-xs rounded-lg hover:bg-[#4A0080] transition"
                      data-testid={`formation-link-${idx}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t.visit}
                    </a>
                  </div>
                </div>
              ))}
              
              {/* Link to full formations page */}
              {filteredFormations.length > 4 && (
                <a
                  href={`/formations?lang=${language}`}
                  className="block text-center text-sm text-[#A4187A] hover:text-[#4A0080] py-2"
                >
                  + {filteredFormations.length - 4} {language === 'nl' ? 'andere opleidingen' : 'autres formations'} →
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
